// Facet glyph library, drawn in a 100x100 box. `tone: 1` is the solid tone,
// `tone: 2` renders at 60% opacity. Parts may set `stroke` (thick squared
// stroke instead of filled facets), `evenodd` (ring shapes with holes),
// and `transform`.
//
// Tiered families encode their count as geometry instead of numerals:
// a line = 1, pentagon = 5, decagon = 10, and polygon rings MULTIPLY their
// contents (pentagon-in-pentagon = 25, decagon-in-decagon = 100, ...).
// Week streaks tally chevrons (1 per week), use the brand X for 10, and
// compose larger counts from X marks and multiplying rings.

const polyPts = (n, r, cx, cy) => {
  const pts = [];
  for (let i = 0; i < n; i++) {
    const a = ((-90 + (i * 360) / n) * Math.PI) / 180;
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return pts;
};

const polyPath = (pts) =>
  `M${pts.map((p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' L')} Z`;

// Split a convex polygon into left/right halves along x = cx for the duotone.
const polyHalves = (pts, cx) => {
  const left = [];
  const right = [];
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    if (p[0] <= cx) {
      left.push(p);
    }
    if (p[0] >= cx) {
      right.push(p);
    }
    if ((p[0] - cx) * (q[0] - cx) < 0) {
      const t = (cx - p[0]) / (q[0] - p[0]);
      const y = p[1] + t * (q[1] - p[1]);
      left.push([cx, y]);
      right.push([cx, y]);
    }
  }
  return [polyPath(left), polyPath(right)];
};

const ring = (n, rOut, rIn, cx, cy) =>
  `${polyPath(polyPts(n, rOut, cx, cy))} ${polyPath(polyPts(n, rIn, cx, cy))}`;

const chev = (cy, half, drop, th) =>
  `M50 ${cy} L${50 - half} ${cy + drop} L${50 - half} ${cy + drop + th} ` +
  `L50 ${cy + th} L${50 + half} ${cy + drop + th} L${50 + half} ${cy + drop} Z`;

// The Praxis logomark (brand-icons/logo-marks/02-PRAXIS_LogoMark-*.svg),
// scaled from its 596x569 viewBox into the glyph box.
const X_LEFT =
  'M 57.5 50 c -6.1 8.9 -18.6 29.1 -24.8 38.1 H 10 ' +
  'C 40.3 50.6 40.4 48.8 10 11.9 h 22.7 c 4.8 6.1 19.7 31.6 24.8 38.1';
const X_RIGHT =
  'M 90 11.9 h -22.7 c -2.8 3.5 -6.9 9.9 -12.6 19 0 0 8 13.8 12.4 19.1 ' +
  '-4.2 5.3 -12.4 19.1 -12.4 19.1 5.7 9.1 9.9 15.4 12.6 19 h 22.7 ' +
  'c -14.4 -17.5 -21.9 -28.6 -22.7 -37.9 0.6 -9.4 8.1 -20.6 22.7 -38.3 Z';

const miniX = (cx, cy, scale) => {
  const transform = `translate(${cx} ${cy}) scale(${scale}) translate(-50 -50)`;
  return [
    { d: X_RIGHT, tone: 2, transform },
    { d: X_LEFT, tone: 1, transform },
  ];
};

// Brand icons (brand-icons/icons/*.svg) are drawn in a 566.56 box; these transforms
// scale each one to fill the 100x100 glyph box, computed from its rendered bounding box.
const BRAND_FIT = {
  alignment: 'translate(-17.54 -17.54) scale(0.2384)',
  certification: 'translate(-24.24 -24.24) scale(0.2621)',
  resources: 'translate(-24.16 -24.16) scale(0.2618)',
  mobility: 'translate(-34.29 -34.29) scale(0.2975)',
};

const BURST_RAY = 'M50 2 L62 24 L50 17 L38 24 Z';

const p5 = polyHalves(polyPts(5, 46, 50, 53), 50);
const p10 = polyHalves(polyPts(10, 46, 50, 50), 50);

const GLYPHS = {
  // ---- Milestones ----
  // First Steps: faceted rising arrow
  arrow: [
    { d: 'M50 6 L82 40 L62 40 L50 27 L38 40 L18 40 Z', tone: 1 },
    { d: 'M40 46 L60 46 L40 94 Z', tone: 2 },
    { d: 'M60 46 L60 94 L40 94 Z', tone: 1 },
  ],
  // Welcome: squared check
  check: [{ d: 'M18 54 L42 76 L84 28', tone: 1, stroke: true }],
  // Anniversary: chevron starburst
  burst: [
    { d: BURST_RAY, tone: 1 },
    { d: BURST_RAY, tone: 1, transform: 'rotate(60 50 50)' },
    { d: BURST_RAY, tone: 1, transform: 'rotate(120 50 50)' },
    { d: BURST_RAY, tone: 1, transform: 'rotate(180 50 50)' },
    { d: BURST_RAY, tone: 1, transform: 'rotate(240 50 50)' },
    { d: BURST_RAY, tone: 1, transform: 'rotate(300 50 50)' },
    { d: 'M50 34 L64 50 L50 66 L36 50 Z', tone: 2 },
  ],
  // Training Logged: forward chevron pair
  chevrons: [
    { d: 'M16 14 L40 50 L16 86 L30 86 L54 50 L30 14 Z', tone: 1 },
    { d: 'M50 14 L74 50 L50 86 L64 86 L88 50 L64 14 Z', tone: 2 },
  ],
  // ---- Memberships: brand icons (brand-icons/icons) in the facet frame ----
  // Apprentice: ALIGNMENT
  // Trainer: CERTIFICATION
  // All-In: RESOURCES
  // AP Member: MOBILITY
  apprentice: [
    {
      d: 'M308.99,260.43c-27.55,11.69-86.93,40.58-115.15,52.54l-51.23-34.43c126.45-42.9,129.4-46.79,117.55-179.8l51.23,34.43c1.32,21.64-3.82,104.04-2.41,127.25',
      tone: 1,
      transform: BRAND_FIT.alignment,
    },
    {
      d: 'M257.57,306.14c27.55-11.69,86.93-40.58,115.15-52.54l51.24,34.43c-126.46,42.89-129.41,46.79-117.56,179.79l-51.23-34.43c-1.32-21.64,3.82-104.04,2.41-127.25',
      tone: 2,
      transform: BRAND_FIT.alignment,
    },
    {
      d: 'M307.86,259.97c-26.91,11.47-84.9,39.85-112.46,51.6l-50.04-33.82c123.5-42.12,126.39-45.94,114.81-176.55l50.04,33.81c1.29,21.25-3.74,102.16-2.35,124.96',
      tone: 1,
      transform: BRAND_FIT.alignment,
    },
  ],
  trainer: [
    {
      d: 'M282.66,218.48c14.46,9.59,37.62,23.82,61.36,38.38,13.52-15.02,21.75-34.89,21.75-56.69,0-46.82-37.96-84.78-84.78-84.78s-84.78,37.96-84.78,84.78c0,22.75,8.98,43.39,23.56,58.62,26.21-16.12,52.01-32.07,62.88-40.32',
      tone: 1,
      transform: BRAND_FIT.certification,
    },
    {
      d: 'M282.89,251.22c22.22,14.73,72.83,44.64,95.58,59.72v54.9c-94.07-72.84-98.4-73.12-190.38.84v-54.9c15.26-11.58,78.43-48.14,94.8-60.57',
      tone: 1,
      transform: BRAND_FIT.certification,
    },
    {
      d: 'M282.89,335.7c22.22,14.73,72.83,44.64,95.58,59.72v54.9c-94.07-72.84-98.4-73.12-190.38.84v-54.9c15.26-11.58,78.43-48.14,94.8-60.57',
      tone: 2,
      transform: BRAND_FIT.certification,
    },
  ],
  allIn: [
    {
      d: 'M301.43,269.65c25.13-17.4,64.28-42.66,75.82-52.21l-.02-59.36c-34.56,30.31-56.63,49.09-75.79,56.42v-95.23h-36.2v95.09c-19.22-7.38-41.68-25.89-76.65-55.44l.02,59.36c17.57,12.72,51.9,35.18,76.63,51.99l10.61,7.23c4.5,3.07,10.44,2.97,14.84-.24,3.11-2.27,6.76-4.85,10.76-7.61',
      tone: 1,
      transform: BRAND_FIT.resources,
    },
    {
      d: 'M298.95,325.75c2.45,30.47,4.64,77.01,7.11,91.78l51.35,29.76c-8.87-45.1-14.04-73.61-10.76-93.87l82.38,47.8,18.17-31.32-82.25-47.72c16.02-12.92,43.3-23.06,86.41-38.48l-51.35-29.76c-19.82,8.82-56.47,27.24-83.43,40.19l-11.58,5.55c-4.91,2.35-7.8,7.54-7.23,12.95.41,3.84.81,8.28,1.19,13.13',
      tone: 2,
      transform: BRAND_FIT.resources,
    },
    {
      d: 'M267.61,325.75c-2.45,30.47-4.64,77.01-7.11,91.78l-51.35,29.76c8.87-45.1,14.04-73.61,10.76-93.87l-82.38,47.8-18.17-31.32,82.25-47.72c-16.02-12.92-43.3-23.06-86.41-38.48l51.35-29.76c19.82,8.82,56.47,27.24,83.43,40.19l11.58,5.55c4.91,2.35,7.8,7.54,7.23,12.95-.41,3.84-.81,8.28-1.19,13.13',
      tone: 2,
      transform: BRAND_FIT.resources,
    },
  ],
  apMember: [
    {
      d: 'M328.84,420.97c-22.11-16.09-72.62-49-95.26-65.48l2.46-56.26c93.13,78.85,97.54,79.34,195.11,7.67l-2.46,56.26c-16.16,11.18-82.51,45.81-99.85,57.81',
      tone: 1,
      transform: BRAND_FIT.mobility,
    },
    {
      d: 'M420.97,237.72c-16.09,22.11-49,72.62-65.48,95.26l-56.26-2.46c78.85-93.13,79.34-97.54,7.67-195.11l56.26,2.46c11.18,16.16,45.81,82.51,57.81,99.85',
      tone: 2,
      transform: BRAND_FIT.mobility,
    },
    {
      d: 'M237.72,145.59c22.11,16.09,72.62,49,95.26,65.48l-2.46,56.26c-93.13-78.85-97.54-79.34-195.11-7.67l2.46-56.26c16.16-11.18,82.51-45.81,99.85-57.81',
      tone: 1,
      transform: BRAND_FIT.mobility,
    },
    {
      d: 'M145.59,328.85c16.09-22.11,49-72.62,65.48-95.26l56.25,2.46c-78.85,93.13-79.33,97.54-7.67,195.11l-56.26-2.46c-11.18-16.16-45.81-82.52-57.81-99.85',
      tone: 2,
      transform: BRAND_FIT.mobility,
    },
  ],

  // Profile Completed: figure over chevrons
  person: [
    { circle: { cx: 50, cy: 22, r: 14 }, tone: 1 },
    { d: 'M50 42 L20 61 L20 74 L50 55 L80 74 L80 61 Z', tone: 2 },
    { d: 'M50 64 L20 83 L20 96 L50 77 L80 96 L80 83 Z', tone: 2 },
  ],

  // ---- Sessions: 1, 5, 10, then rings multiply ----
  s1: [{ d: 'M62 12 L76 26 L38 88 L24 74 Z', tone: 1 }],
  s5: [
    { d: p5[0], tone: 1 },
    { d: p5[1], tone: 2 },
  ],
  s10: [
    { d: p10[0], tone: 1 },
    { d: p10[1], tone: 2 },
  ],
  s25: [
    { d: ring(5, 46, 33, 50, 53), tone: 2, evenodd: true },
    { d: polyPath(polyPts(5, 20, 50, 52)), tone: 1 },
  ],
  s50: [
    { d: ring(10, 46, 34, 50, 50), tone: 2, evenodd: true },
    { d: polyPath(polyPts(5, 20, 50, 51)), tone: 1 },
  ],
  s100: [
    { d: ring(10, 46, 34, 50, 50), tone: 2, evenodd: true },
    { d: polyPath(polyPts(10, 20, 50, 50)), tone: 1 },
  ],
  s200: [
    { d: ring(10, 46, 36, 50, 50), tone: 2, evenodd: true },
    { d: ring(10, 28, 18, 50, 50), tone: 1, evenodd: true },
    { d: 'M45 43 L49 43 L49 57 L45 57 Z', tone: 1 },
    { d: 'M51 43 L55 43 L55 57 L51 57 Z', tone: 1 },
  ],
  s500: [
    { d: ring(10, 46, 36, 50, 50), tone: 2, evenodd: true },
    { d: ring(10, 28, 18, 50, 50), tone: 1, evenodd: true },
    { d: polyPath(polyPts(5, 11, 50, 51)), tone: 1 },
  ],
  s1000: [
    { d: ring(10, 46, 36, 50, 50), tone: 2, evenodd: true },
    { d: ring(10, 28, 18, 50, 50), tone: 1, evenodd: true },
    { d: polyPath(polyPts(10, 11, 50, 50)), tone: 1 },
  ],
  s2500: [
    { d: ring(10, 46, 38, 50, 50), tone: 2, evenodd: true },
    { d: ring(10, 31, 24, 50, 50), tone: 1, evenodd: true },
    { d: ring(5, 17, 11.5, 50, 50.8), tone: 2, evenodd: true },
    { d: polyPath(polyPts(5, 5.5, 50, 50.4)), tone: 1 },
  ],
  s5000: [
    { d: ring(10, 46, 36, 50, 50), tone: 2, evenodd: true },
    { d: ring(10, 28, 18, 50, 50), tone: 1, evenodd: true },
    {
      d: `${polyPath(polyPts(10, 12, 50, 50))} ${polyPath(polyPts(5, 6, 50, 50.5))}`,
      tone: 1,
      evenodd: true,
    },
  ],
  s10000: [
    { d: ring(10, 46, 38, 50, 50), tone: 2, evenodd: true },
    { d: ring(10, 31, 24, 50, 50), tone: 1, evenodd: true },
    { d: ring(10, 17, 11, 50, 50), tone: 2, evenodd: true },
    { d: polyPath(polyPts(10, 5.5, 50, 50)), tone: 1 },
  ],

  // ---- Week streaks: chevron tallies, X = 10 ----
  w1: [{ d: chev(32, 34, 24, 16), tone: 1 }],
  w2: [
    { d: chev(18, 34, 23, 14), tone: 1 },
    { d: chev(46, 34, 23, 14), tone: 2 },
  ],
  w3: [
    { d: chev(10, 34, 21, 12), tone: 1 },
    { d: chev(36, 34, 21, 12), tone: 2 },
    { d: chev(62, 34, 21, 12), tone: 2 },
  ],
  w5: [
    { d: chev(6, 33, 17, 7), tone: 1 },
    { d: chev(23, 33, 17, 7), tone: 2 },
    { d: chev(40, 33, 17, 7), tone: 2 },
    { d: chev(57, 33, 17, 7), tone: 2 },
    { d: chev(74, 33, 17, 7), tone: 2 },
  ],
  w10: [
    { d: X_RIGHT, tone: 2 },
    { d: X_LEFT, tone: 1 },
  ],
  w20: [
    { d: chev(2, 25, 11, 5), tone: 1 },
    { d: chev(13, 25, 11, 5), tone: 1 },
    { d: chev(24, 25, 11, 5), tone: 1 },
    { d: chev(35, 25, 11, 5), tone: 1 },
    { d: chev(46, 25, 11, 5), tone: 1 },
    ...miniX(50, 78, 0.44),
  ],
  w30: [...miniX(28.5, 50, 0.27), ...miniX(50, 50, 0.27), ...miniX(71.5, 50, 0.27)],
  w50: [{ d: ring(5, 46, 34, 50, 53), tone: 2, evenodd: true }, ...miniX(50, 52, 0.46)],
  w100: [{ d: ring(10, 46, 34, 50, 50), tone: 2, evenodd: true }, ...miniX(50, 50, 0.46)],
  w200: [
    { d: ring(10, 46, 34, 50, 50), tone: 2, evenodd: true },
    ...miniX(38.5, 50, 0.3),
    ...miniX(61.5, 50, 0.3),
  ],
  w300: [
    { d: ring(10, 46, 34, 50, 50), tone: 2, evenodd: true },
    ...miniX(34, 50, 0.22),
    ...miniX(50, 50, 0.22),
    ...miniX(66, 50, 0.22),
  ],
  w500: [
    { d: ring(5, 46, 36, 50, 53), tone: 2, evenodd: true },
    { d: ring(10, 28, 20, 50, 51), tone: 1, evenodd: true },
    ...miniX(50, 51, 0.24),
  ],
};

export default GLYPHS;
